import * as api from "@/api";
import { readTextFile } from "@/file";
import globals from "@/globals";
import * as signals from "@/signals";
import { getDomain } from "@/site";
import { closeDialog, showDialog, sleep } from "@/utils";
import { t } from "i18next";

class Ssl {
	private readonly domain: string;

	public constructor() {
		this.domain = getDomain(signals.currentSite.value);
	}

	public applyCertificate(orderUrl: string, hasRetried = false): void {
		let seconds = hasRetried ? 540 : 60;
		void showDialog(
			t("certificateCountdownTip", {
				seconds: seconds,
			}),
			{
				disableOk: true,
			},
		);
		const countdownId = setInterval(() => {
			void (async (): Promise<void> => {
				if (seconds <= 0) {
					clearInterval(countdownId);
					return;
				}
				seconds--;
				signals.dialog.value = {
					...signals.dialog.value,
					text: t("certificateCountdownTip", {
						seconds: seconds,
					}),
				};
				if (seconds > 0) {
					return;
				}
				clearInterval(countdownId);
				void closeDialog();
				signals.isLoadingShown.value = true;
				try {
					const data = await api.applySslCertificate({
						domain: this.domain,
						orderUrl: orderUrl,
					});
					if (data.alert) {
						if (!hasRetried && data.canRetry) {
							this.applyCertificate(orderUrl, true);
						} else {
							void showDialog(data.alert);
						}
					} else if (data.success) {
						void showDialog(t("certificateIssuedSuccessfully"));
					}
				} catch (error) {
					void api.handleApiError(error);
				} finally {
					signals.isLoadingShown.value = false;
				}
			})();
		}, 1000);
	}

	public async createCertificate(): Promise<void> {
		signals.isLoadingShown.value = true;
		try {
			const data = await api.createSslCertificate(this.domain);
			if (!data) {
				return;
			}
			if (data.alert) {
				await sleep(globals.ANIMATION_WAIT_TIME);
				void showDialog(data.alert);
			} else if (data.orderUrl) {
				this.applyCertificate(data.orderUrl);
			}
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	}

	public async deleteCertificate(): Promise<void> {
		await showDialog(t("confirmDeleteExistingSslCertificate"), {
			showCancel: true,
		});
		signals.isLoadingShown.value = true;
		try {
			const data = await api.deleteSslCertificate(this.domain);
			await sleep(globals.ANIMATION_WAIT_TIME);
			if (data.alert) {
				void showDialog(data.alert);
			} else if (data.success) {
				void showDialog(t("deletedSuccessfully"));
			}
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	}

	public async uploadCertificate(
		publicKey: File,
		privateKey: File,
	): Promise<void> {
		signals.isLoadingShown.value = true;
		try {
			const data = await api.uploadSslCertificate({
				domain: this.domain,
				privateKey: await readTextFile(privateKey),
				publicKey: await readTextFile(publicKey),
			});
			if (data.alert) {
				void showDialog(data.alert);
			} else if (data.success) {
				void showDialog(t("uploadedSuccessfully"));
			}
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	}
}

export default Ssl;
